/**
 * Custom Upload Adapter for CKEditor 5
 *
 * Handles image uploads to the server via the uploadApi.
 * This adapter integrates with the CMS's upload system.
 */

import { uploadApi } from '../../../context/api/uploadApi';
import type { UploadFile } from '../../../context/models/upload/UploadFile';

class CustomUploadAdapter {
	private loader: {
		file: Promise<File>;
	};

	constructor(loader: { file: Promise<File> }) {
		this.loader = loader;
	}

	upload(): Promise<{ default: string }> {
		return this.loader.file
			.then((file: File) => {
				return uploadApi
					.UploadImagesAsync({ files: [file], saveName: '' })
					.then((response) => {
						if (response && response.isSuccess) {
							const images = response.result as UploadFile[];
							if (images && images.length > 0) {
								return {
									default: images[0]?.path || '',
								};
							}
						}
						return Promise.reject(new Error(response?.message || 'Upload failed'));
					});
			})
			.catch((error: Error) => {
				console.error('CKEditor upload error:', error);
				return Promise.reject(error);
			});
	}

	abort(): void {
		// Handle abort if needed
	}
}

export function uploadAdapterPlugin(editor: { plugins: { get: (name: string) => { createUploadAdapter: (loader: { file: Promise<File> }) => CustomUploadAdapter } } }): void {
	editor.plugins.get('FileRepository').createUploadAdapter = (loader: { file: Promise<File> }) => {
		return new CustomUploadAdapter(loader);
	};
}

export function createUploadAdapter(loader: { file: Promise<File> }): CustomUploadAdapter {
	return new CustomUploadAdapter(loader);
}
